// TelcoTrack — Digitální cesta path termination specs (MSO / Zřízené VR).
// Reuses závrt `UKONCENI_V_RD_*` for „kde ukončeno“.

import {
  isUkonceniVRdSelectionValid,
  type UkonceniVRd,
} from './ukonceni-v-rd.constants';

/** One digital path: floor (patro) + termination place (ukončení). */
export interface DigitalniCestaPathSpec {
  /** 1-based index matching `digitalniCesta` count (1..N). */
  pathIndex: number;
  /**
   * Floor / patro stored as composed label (e.g. „1. NP“, „2. PP“) for BE/compat.
   * UI edits number + NP/PP toggle; see {@link parseDigitalniCestaPatro} /
   * {@link formatDigitalniCestaPatro}.
   */
  patro: string;
  /** Same enum as závrt ukončení v RD. */
  ukonceni: UkonceniVRd | string;
  /** Required non-empty when `ukonceni === 'JINE'`. */
  ukonceniOther?: string | null;
}

/** Parsed UI parts for digitální cesta patro (number + NP/PP). */
export interface DigitalniCestaPatroParts {
  /** Positive floor ordinal when parseable; null if empty/unparseable. */
  floorNumber: number | null;
  /** true = NP (nadzemní), false = PP (podzemní). Defaults to NP when unknown. */
  aboveGround: boolean;
}

/** Canonical storage shape matching VSO floor labels: `"1. NP"` / `"2. PP"`. */
export function formatDigitalniCestaPatro(
  floorNumber: number,
  aboveGround: boolean,
): string {
  const n = Math.trunc(Number(floorNumber));
  if (!Number.isFinite(n) || n < 1) return '';
  return `${n}. ${aboveGround ? 'NP' : 'PP'}`;
}

/**
 * Parse stored patro for number + NP/PP UI.
 * Accepts `"1. NP"`, `"1.NP"`, `"2.PP"`, `"2 PP"`; legacy free-text falls back to
 * best-effort number extraction and PP detection (else NP).
 */
export function parseDigitalniCestaPatro(
  patro: string | null | undefined,
): DigitalniCestaPatroParts {
  const raw = String(patro ?? '').trim();
  if (!raw) return { floorNumber: null, aboveGround: true };

  const structured = raw.match(/^(\d+)\s*\.?\s*(NP|PP)\s*$/i);
  if (structured) {
    const floorNumber = Math.trunc(Number(structured[1]));
    return {
      floorNumber: Number.isFinite(floorNumber) && floorNumber >= 1 ? floorNumber : null,
      aboveGround: structured[2].toUpperCase() === 'NP',
    };
  }

  const hasPp = /(^|[^A-Za-zÁ-ž])PP([^A-Za-zÁ-ž]|$)/i.test(raw);
  const numMatch = raw.match(/(\d+)/);
  const parsedNum = numMatch ? Math.trunc(Number(numMatch[1])) : NaN;
  return {
    floorNumber: Number.isFinite(parsedNum) && parsedNum >= 1 ? parsedNum : null,
    aboveGround: !hasPp,
  };
}

export const DIGITALNI_CESTA_SPEC_INCOMPLETE_MESSAGE =
  'Každá digitální cesta musí mít číslo patra (NP/PP) a místo ukončení.';

/** Czech hint under Patro number + NP/PP toggle (web / Android). */
export const DIGITALNI_CESTA_PATRO_HINT =
  'Zadejte číslo patra a zvolte NP (nadzemní) nebo PP (podzemní). Samotné „NP“ / „PP“ nestačí.';

/**
 * Protocol / parties field `np`: digits-only floor ordinal (e.g. `"2"`).
 * Rejects bare „pp“ / „NP“; accepts legacy `"1. NP"` by extracting the number.
 */
export const CONTRACT_PARTY_NP_HINT_CS =
  'Zadejte číslo patra (např. 2). Nevyplňujte text „NP“ ani „PP“.';

export const CONTRACT_PARTY_NP_PLACEHOLDER_CS = 'např. 2';

/** True when patro parses to a positive floor ordinal (not empty / not bare „pp“). */
export function isDigitalniCestaPatroValid(
  patro: string | null | undefined,
): boolean {
  return parseDigitalniCestaPatro(patro).floorNumber != null;
}

/**
 * Normalize parties `np` to digits-only ordinal, or null when empty/invalid
 * (e.g. „pp“ → null, „1. NP“ → `"1"`, `"2"` → `"2"`).
 */
export function normalizeContractPartyNpValue(
  raw: string | null | undefined,
): string | null {
  const n = parseDigitalniCestaPatro(raw).floorNumber;
  return n != null ? String(n) : null;
}

export function emptyDigitalniCestaPathSpec(pathIndex: number): DigitalniCestaPathSpec {
  return {
    pathIndex,
    patro: '',
    ukonceni: '',
    ukonceniOther: null,
  };
}

/** Normalize raw API/DB value into a clean array (drops junk entries). */
export function normalizeDigitalniCestaSpec(
  raw: unknown,
): DigitalniCestaPathSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: DigitalniCestaPathSpec[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const pathIndex = Math.trunc(Number(rec['pathIndex'] ?? rec['path_index'] ?? 0));
    if (!Number.isFinite(pathIndex) || pathIndex < 1) continue;
    const patro = String(rec['patro'] ?? '').trim();
    const ukonceni = String(rec['ukonceni'] ?? '').trim();
    const otherRaw = rec['ukonceniOther'] ?? rec['ukonceni_other'];
    const ukonceniOther =
      otherRaw == null ? null : String(otherRaw).trim() || null;
    out.push({
      pathIndex,
      patro,
      ukonceni,
      ukonceniOther,
    });
  }
  out.sort((a, b) => a.pathIndex - b.pathIndex);
  return out;
}

/**
 * Resize path specs to match `count` (0..N). Preserves existing entries by pathIndex;
 * appends empty slots; drops extras beyond count. Reindexes densely to 1..count.
 */
export function resizeDigitalniCestaSpec(
  existing: DigitalniCestaPathSpec[] | null | undefined,
  count: number,
): DigitalniCestaPathSpec[] {
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  if (n === 0) return [];
  const byIndex = new Map<number, DigitalniCestaPathSpec>();
  for (const item of normalizeDigitalniCestaSpec(existing ?? [])) {
    if (!byIndex.has(item.pathIndex)) byIndex.set(item.pathIndex, item);
  }
  const out: DigitalniCestaPathSpec[] = [];
  for (let i = 1; i <= n; i++) {
    const prev = byIndex.get(i);
    if (prev) {
      out.push({
        pathIndex: i,
        patro: prev.patro ?? '',
        ukonceni: prev.ukonceni ?? '',
        ukonceniOther: prev.ukonceni === 'JINE' ? (prev.ukonceniOther ?? null) : null,
      });
    } else {
      out.push(emptyDigitalniCestaPathSpec(i));
    }
  }
  return out;
}

/** Single path: parseable patro number + NP/PP + valid ukončení (incl. JINE other). */
export function isDigitalniCestaPathSpecValid(
  item: DigitalniCestaPathSpec | null | undefined,
): boolean {
  if (!item) return false;
  if (!isDigitalniCestaPatroValid(item.patro)) return false;
  return isUkonceniVRdSelectionValid(item.ukonceni, item.ukonceniOther);
}

/**
 * Specs are valid for a given path count:
 * - count 0 → empty / missing array OK
 * - count N → exactly N entries (pathIndex 1..N), each fully filled
 */
export function isDigitalniCestaSpecValid(
  count: number | null | undefined,
  specs: DigitalniCestaPathSpec[] | null | undefined,
): boolean {
  const n = Math.trunc(Number(count ?? 0));
  if (!Number.isFinite(n) || n < 0) return false;
  if (n === 0) return true;
  const list = resizeDigitalniCestaSpec(specs, n);
  if (list.length !== n) return false;
  return list.every((item, idx) => item.pathIndex === idx + 1 && isDigitalniCestaPathSpecValid(item));
}

/** Sanitize for persistence: length = count, clear other unless JINE, trim/canonicalize patro. */
export function sanitizeDigitalniCestaSpecForSave(
  count: number | null | undefined,
  specs: DigitalniCestaPathSpec[] | null | undefined,
): DigitalniCestaPathSpec[] {
  const n = Math.max(0, Math.trunc(Number(count ?? 0)) || 0);
  return resizeDigitalniCestaSpec(specs, n).map((item) => {
    const trimmed = (item.patro ?? '').trim();
    const parts = parseDigitalniCestaPatro(trimmed);
    // Drop unparseable free-text (e.g. bare „pp“) — must be number + NP/PP.
    const patro =
      parts.floorNumber != null
        ? formatDigitalniCestaPatro(parts.floorNumber, parts.aboveGround)
        : '';
    return {
      pathIndex: item.pathIndex,
      patro,
      ukonceni: (item.ukonceni ?? '').trim(),
      ukonceniOther:
        (item.ukonceni ?? '').trim() === 'JINE'
          ? ((item.ukonceniOther ?? '').trim() || null)
          : null,
    };
  });
}
